import type {
    AnalyticsButtonInfo,
    EventSeriesData,
    NextLocale,
    TicketTimeCtaData,
    VenueId,
} from '@beat-venues/common-types';
import { type VariantProps, tv } from 'tailwind-variants';

import type { CommonProps } from '../../../types';
import { Heading, StatusLabel, Text } from '@beat-venues/ui/subset';
import { Button, TextLink } from '../../../atoms';
import { useTranslation } from 'react-i18next';
import { getEventDetailLink } from '../../../utils/domain-util';

import type { JSX } from 'react';

export interface EventCompactCardItemProps
    extends CommonProps,
        VariantProps<typeof EventCompactCardItemStyles>,
        Pick<EventSeriesData, 'date' | 'slug' | 'image'> {
    name?: string;
    venueId?: VenueId;
    ctaButton?: TicketTimeCtaData;
    ctaTextLink: { url?: string; label: string };
    onBuyTicketClick?: (value: AnalyticsButtonInfo) => void;
    eventId: string;
    buttonVariant?: 'primary' | 'mastercard';
    status?: string;
    venueName?: string;
    endSalesDate?: string;
}

export const EventCompactCardItemStyles = tv({
    slots: {
        root: 'relative bg-gray-500 rounded-md flex max-md:flex-col gap-6 overflow-hidden md:h-[140px]',
        statusLabel: 'absolute top-1 left-1',
        infoWrapper: 'grow my-auto max-md:px-4 flex flex-col',
        moreInfoCta: 'self-start',
        buttonWrapper:
            'min-w-[300px] flex flex-col justify-center items-center gap-0 md:mr-2 mb-2 md:mb-0 md:gap-4 mt-4',
        button: 'self-center mx-2 lg:mx-4 shrink-0 relative max-md:mb-6',
    },
});

export const EventCompactCardItem = ({
    className,
    image,
    name,
    eventId,
    date,
    status,
    venueId,
    venueName,
    ctaTextLink,
    ctaButton,
    buttonVariant,
    endSalesDate,
    testId = 'event-card',
    onBuyTicketClick,
}: EventCompactCardItemProps): JSX.Element => {
    const { t, i18n } = useTranslation();

    const {
        root,
        statusLabel: statusLabelStyle,
        button,
        buttonWrapper,
        infoWrapper,
        moreInfoCta,
    } = EventCompactCardItemStyles();

    const getEventVenueUrl = () => {
        if (!ctaTextLink.url || !venueId) return undefined;
        return getEventDetailLink(
            venueId,
            ctaTextLink.url,
            i18n.language as NextLocale
        );
    };

    return (
        <div className={root({ className })} data-testid={testId}>
            <div className="lg:w-[249px]">
                {status && (
                    <StatusLabel className={statusLabelStyle()}>
                        {status}
                    </StatusLabel>
                )}
                <div className="aspect-video h-full">
                    {image && (
                        <img
                            alt={image.alt}
                            className="h-full object-cover"
                            height="1080"
                            src={image.url}
                            width="1920"
                        />
                    )}
                </div>
            </div>
            <div className={infoWrapper()}>
                <Heading as="h2" className="mb-0" size="h5">
                    {name}
                </Heading>
                {date && <Text>{date}</Text>}

                <Text>{venueId ? t(`venues.${venueId}.name`) : venueName}</Text>

                {getEventVenueUrl() && (
                    <TextLink
                        className={moreInfoCta()}
                        size="sm"
                        underline={true}
                        href={getEventVenueUrl()}
                    >
                        {ctaTextLink.label}
                    </TextLink>
                )}
            </div>
            {ctaButton && (
                <div className={buttonWrapper()}>
                    <Button
                        className={button()}
                        disabled={ctaButton.isDisabled}
                        href={ctaButton.url}
                        intent={buttonVariant}
                        target="_blank"
                        onClick={() => {
                            const buttonInfo: AnalyticsButtonInfo = {
                                event: 'button_click',
                                id: eventId,
                                name: 'buy tickets',
                                text: t(
                                    `events.ctaStatusLabels.${ctaButton.label}`
                                ),
                                category: 'event ticket link',
                                type: 'cta',
                                target_url: ctaButton?.url || '',
                            };
                            if (onBuyTicketClick) {
                                onBuyTicketClick(buttonInfo);
                            }
                        }}
                    >
                        <span>
                            {/\d/.test(ctaButton.label)
                                ? ctaButton.label
                                : t(
                                      `events.ctaStatusLabels.${ctaButton.label}`
                                  )}
                        </span>
                    </Button>
                    {endSalesDate && !ctaButton.isDisabled && (
                        <Text className="text-vn-brand-500">
                            {t('timings.endPresales', { date: endSalesDate })}
                        </Text>
                    )}
                </div>
            )}
        </div>
    );
};
